import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Plus, Minus, Check, Sparkles, Trash, Circle, CheckCircle2, Store, ShoppingCart } from 'lucide-react';
import { useAppStore } from '../../shared/store';
import { generateId } from '../../shared/utils/id';
import { toast } from '../../shared/components/Toast';
import { ShoppingListItem, InventoryItem, Transaction } from '../../shared/types';
import { AIListGenerator } from './AIListGenerator';
import { CompleteTripDialog } from './components/CompleteTripDialog';
interface CompletionSnapshot {
  inventory: InventoryItem[];
  shoppingList: ShoppingListItem[];
  transactions: Transaction[];
}
export function ShopPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [showCompleteTripDialog, setShowCompleteTripDialog] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const {
    shoppingList,
    inventory,
    stores,
    addShoppingListItem,
    removeShoppingListItem,
    updateShoppingListItem,
    toggleShoppingListItem,
    setShoppingList,
    updateInventoryItem,
    addInventoryItem,
    addTransaction,
    setInventory,
    transactions,
    setTransactions
  } = useAppStore();
  // Search results
  const searchResults = useMemo(() => {
    if (searchQuery.length < 2) return [];
    return inventory.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()) && !shoppingList.some(listItem => listItem.name.toLowerCase() === item.name.toLowerCase()));
  }, [searchQuery, inventory, shoppingList]);
  const handleAddItem = (item: InventoryItem) => {
    addShoppingListItem({
      id: generateId(),
      name: item.name,
      quantity: 1,
      unit: item.unit,
      estimatedPrice: item.price,
      store: stores[0]?.name || 'Overig',
      priority: 'medium',
      category: item.category,
      isCompleted: false
    });
    setSearchQuery('');
    toast.success(`${item.name} toegevoegd`);
  };
  const handleUpdateQuantity = (id: string, current: number, change: number) => {
    const newQuantity = Math.max(1, current + change);
    updateShoppingListItem(id, {
      quantity: newQuantity
    });
  };
  const handleConfirmTrip = (actualTotals: Record<string, number>) => {
    if (shoppingList.length === 0) return;
    const snapshot: CompletionSnapshot = {
      inventory,
      shoppingList,
      transactions
    };
    const completedAt = new Date().toISOString();
    const itemsByStore = groupItemsByStore(shoppingList);
    shoppingList.forEach(listItem => {
      const existingItem = inventory.find(item => item.name.toLowerCase() === listItem.name.toLowerCase());
      if (existingItem) {
        const newQuantity = existingItem.quantity + listItem.quantity;
        updateInventoryItem(existingItem.id, {
          quantity: newQuantity,
          currentStock: newQuantity,
          lastRestocked: completedAt,
          stockStatus: newQuantity >= existingItem.maxQuantity ? 'high' : 'medium'
        });
        return;
      }
      addInventoryItem({
        id: generateId(),
        name: listItem.name,
        category: listItem.category || 'Voorraadkast',
        quantity: listItem.quantity,
        unit: listItem.unit,
        minQuantity: Math.max(1, Math.floor(listItem.quantity / 2)),
        maxQuantity: listItem.quantity * 2,
        location: 'Voorraadkast',
        lastRestocked: completedAt,
        price: listItem.estimatedPrice,
        bulkEligible: false,
        averageShelfLife: 30,
        consumptionRate: 0,
        storageRequirement: 1,
        currentStock: listItem.quantity,
        totalWeight: 0,
        stockStatus: 'medium'
      });
    });
    Object.entries(itemsByStore).forEach(([storeName, storeItems]) => {
      const actualTotal = actualTotals[storeName];
      const retailerId = stores.find(store => store.name === storeName)?.id;
      addTransaction({
        id: generateId(),
        store: storeName,
        items: storeItems.map(item => item.name).join(', '),
        amount: actualTotal,
        date: completedAt,
        type: 'expense',
        originalPrice: actualTotal,
        discountedPrice: actualTotal,
        savings: 0,
        retailerId: retailerId || generateId()
      });
    });
    setShoppingList([]);
    setShowCompleteTripDialog(false);
    toast.success('Boodschappen verwerkt', {
      description: 'Voorraad en uitgaven zijn per winkel bijgewerkt.',
      action: {
        label: 'Ongedaan maken',
        onClick: () => {
          setInventory(snapshot.inventory);
          setShoppingList(snapshot.shoppingList);
          setTransactions(snapshot.transactions);
          toast.success('Je boodschappenronde is hersteld');
        }
      },
      duration: 8000
    });
  };
  const handleAIGenerate = (items: any[]) => {
    items.forEach(item => {
      addShoppingListItem({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        estimatedPrice: item.estimatedPrice,
        store: item.store,
        priority: 'medium',
        category: item.category,
        isCompleted: false
      });
    });
    setShowAIGenerator(false);
    toast.success(`${items.length} items toegevoegd aan je lijst! ✨`);
  };
  const totalCost = useMemo(() => {
    return shoppingList.reduce((total, item) => total + item.estimatedPrice * item.quantity, 0);
  }, [shoppingList]);
  // Group by store, separate completed
  const {
    activeGroups,
    completedItems
  } = useMemo(() => {
    const active: Record<string, ShoppingListItem[]> = {};
    const completed: ShoppingListItem[] = [];
    shoppingList.forEach(item => {
      if (item.isCompleted) {
        completed.push(item);
      } else {
        const store = item.store || 'Overig';
        if (!active[store]) active[store] = [];
        active[store].push(item);
      }
    });
    return {
      activeGroups: active,
      completedItems: completed
    };
  }, [shoppingList]);
  return <div className="min-h-screen bg-background pb-32" data-id="element-2115" data-mp="4r56z5q290k3">
      {/* Header & Search */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border pt-6 pb-4 px-6 space-y-4" data-id="element-2116" data-mp="9pgh7hk9pd2u">
        <h1 className="text-2xl font-bold text-foreground" data-id="element-2117" data-mp="xgc6a6xc9u6e">Boodschappen</h1>

        <div className="relative" data-id="element-2118" data-mp="12e46do850mlo">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" data-id="element-2119" data-mp="12d2n4wgywtsv" />
          <input type="text" placeholder="Zoek of voeg toe..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder-muted-foreground" data-id="element-2120" data-mp="noohkds62r8m" />

          {/* Search Dropdown */}
          {searchQuery.length > 0 && <div className="absolute z-30 left-0 right-0 mt-2 p-2 bg-card rounded-2xl border border-border shadow-xl max-h-60 overflow-y-auto" data-id="element-2121" data-mp="30wfpmu22pg1s">
              {searchResults.map(item => <button key={item.id} onClick={() => handleAddItem(item)} className="w-full p-3 flex items-center justify-between hover:bg-accent rounded-lg transition-colors text-left" data-id="element-2122" data-mp="3am93k1pou797">
                  <div data-id="element-2123" data-mp="2hz67rhcecamn">
                    <p className="font-medium text-foreground" data-id="element-2124" data-mp="2hzodr5ov237r">{item.name}</p>
                    <p className="text-xs text-muted-foreground" data-id="element-2125" data-mp="2vyec0onf28xp">
                      Voorraad: {item.quantity} {item.unit}
                    </p>
                  </div>
                  <Plus className="w-5 h-5 text-primary" data-id="element-2126" data-mp="b5u8g7wo2g5s" />
                </button>)}

              <button onClick={() => {
            addShoppingListItem({
              id: generateId(),
              name: searchQuery,
              quantity: 1,
              unit: 'stuk',
              estimatedPrice: 0,
              store: stores[0]?.name || 'Overig',
              priority: 'medium',
              isCompleted: false
            });
            setSearchQuery('');
            toast.success(`${searchQuery} toegevoegd`);
          }} className="w-full p-3 flex items-center gap-3 hover:bg-accent rounded-lg transition-colors text-left mt-1 border-t border-border pt-3" data-id="element-2127" data-mp="66zj0n471tcp">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-2128" data-mp="1izcei6179ggy">
                  <Plus className="w-4 h-4 text-primary" data-id="element-2129" data-mp="1my1xqf8x5s8i" />
                </div>
                <div data-id="element-2130" data-mp="3krqu2yg9u8u7">
                  <p className="font-medium text-primary" data-id="element-2131" data-mp="3ksaeqmxb3bbx">
                    Voeg "{searchQuery}" toe
                  </p>
                  <p className="text-xs text-muted-foreground" data-id="element-2132" data-mp="3h8jnjhp8l9m0">Nieuw product</p>
                </div>
              </button>
            </div>}
        </div>
      </div>

      <div className="px-6 pt-4 space-y-6" data-id="element-2133" data-mp="3cfgurbsaa46v">
        {/* Suggested shopping action */}
        <div className="flex items-center justify-between gap-4 rounded-2xl border border-primary/20 bg-primary/10 p-4" data-id="element-2134" data-mp="37g48as5bqz4t">
          <div data-id="element-2135" data-mp="41hzydh1souy">
            <h3 className="flex items-center gap-1.5 text-sm font-bold text-primary" data-id="element-2136" data-mp="42019oikht9q">
              <Sparkles className="h-4 w-4" data-id="element-2137" data-mp="3v816hgfupsng" /> Lijst aanvullen
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-2138" data-mp="3v5yj74632dmf">
              Bekijk wat je nodig hebt en welke aanbiedingen passen.
            </p>
          </div>
          <button onClick={() => setShowAIGenerator(true)} className="shrink-0 whitespace-nowrap rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90" data-id="element-2139" data-mp="3m6l681ir36wy">
            Bekijk suggesties
          </button>
        </div>

        {/* Empty State */}
        {shoppingList.length === 0 && <div className="py-12 text-center" data-id="element-2140" data-mp="236m8y2di98pl">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent" data-id="element-2141" data-mp="2gwppqdtm8a7n">
              <ShoppingCart className="h-8 w-8 text-muted-foreground" data-id="element-2142" data-mp="2c69duy5g0wne" />
            </div>
            <p className="font-semibold text-foreground" data-id="element-2143" data-mp="2qux5itc64m82">Nog niets op je lijst</p>
            <p className="mt-1 text-sm text-muted-foreground" data-id="element-2144" data-mp="2qx04i2vatpox">
              Zoek een product, of bekijk onze suggesties hierboven.
            </p>
          </div>}

        {/* Active Items Grouped by Store */}
        <div className="space-y-6" data-id="element-2145" data-mp="2vu09eagewmeg">
          {Object.entries(activeGroups).map(([store, items]) => <div key={store} className="space-y-2" data-id="element-2146" data-mp="15hatxdd57oe4">
              <div className="flex items-center gap-2 px-1" data-id="element-2147" data-mp="15i2p4n2tvk3v">
                <Store className="w-4 h-4 text-muted-foreground" data-id="element-2148" data-mp="11ghaaw6qgvr1" />
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider" data-id="element-2149" data-mp="11ibto62cwft0">
                  {store}
                </h3>
              </div>
              <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border" data-id="element-2150" data-mp="vd49wo7e38nx">
                <AnimatePresence initial={false} data-id="element-2151" data-mp="hk4t8ivvf8es">
                  {items.map(item => <motion.div key={item.id} layout initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-2152" data-mp="mjhgv5ukgjif">
                      <div className="p-4 flex items-center gap-3 cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setExpandedItemId(expandedItemId === item.id ? null : item.id)} data-id="element-2153" data-mp="1y0qz5p1yl5i5">
                        <button onClick={e => {
                    e.stopPropagation();
                    toggleShoppingListItem(item.id);
                  }} className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary" aria-label={`${item.name} afvinken`} data-id="element-2154" data-mp="1hmazob7l2srm">
                          <Circle className="w-7 h-7" data-id="element-2155" data-mp="2y12ralwwnvzv" />
                        </button>
                        <div className="flex-1 min-w-0" data-id="element-2156" data-mp="2t8jdpoy7x0v2">
                          <p className="font-medium text-foreground truncate" data-id="element-2157" data-mp="30kqrlbwj25yh">
                            {item.name}
                          </p>
                          <p className="text-xs text-muted-foreground" data-id="element-2158" data-mp="35klgxb0nmnhf">
                            {item.quantity} {item.unit}
                          </p>
                        </div>
                        <div className="text-right" data-id="element-2159" data-mp="3fj197hbf42si">
                          <p className="font-medium text-foreground" data-id="element-2160" data-mp="3ffn8ediek1n6">
                            €{(item.estimatedPrice * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {/* Expanded Controls */}
                      <AnimatePresence data-id="element-2161" data-mp="3tbhgiubcqmz8">
                        {expandedItemId === item.id && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="bg-accent/30 px-4 pb-4 pt-1 flex items-center justify-between" data-id="element-2162" data-mp="25698gfr0pzg">
                            <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1" data-id="element-2163" data-mp="ptim4kx8dzvq">
                              <button onClick={() => handleUpdateQuantity(item.id, item.quantity, -1)} className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-accent transition-colors" data-id="element-2164" data-mp="kvh5e6na2cnf">
                                <Minus className="w-4 h-4 text-foreground" data-id="element-2165" data-mp="2dqi2pts5l2yn" />
                              </button>
                              <span className="w-8 text-center font-medium text-foreground text-sm" data-id="element-2166" data-mp="2hty1avns9ddz">
                                {item.quantity}
                              </span>
                              <button onClick={() => handleUpdateQuantity(item.id, item.quantity, 1)} className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-accent transition-colors" data-id="element-2167" data-mp="28tj9rek87221">
                                <Plus className="w-4 h-4 text-foreground" data-id="element-2168" data-mp="3fg54q28cozor" />
                              </button>
                            </div>
                            <button onClick={() => removeShoppingListItem(item.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors" data-id="element-2169" data-mp="3u3ltkx5lwecz">
                              <Trash className="w-4 h-4" data-id="element-2170" data-mp="3k3v3d7o2wlip" />
                            </button>
                          </motion.div>}
                      </AnimatePresence>
                    </motion.div>)}
                </AnimatePresence>
              </div>
            </div>)}
        </div>

        {/* Completed Items */}
        {completedItems.length > 0 && <div className="space-y-2 pt-4" data-id="element-2171" data-mp="31cvjgkvfy1wa">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider px-1" data-id="element-2172" data-mp="2wgmw4k73b2e1">
              Afgevinkt
            </h3>
            <div className="bg-card/50 border border-border/50 rounded-2xl overflow-hidden divide-y divide-border/50" data-id="element-2173" data-mp="2sdgq9dnw3d83">
              <AnimatePresence initial={false} data-id="element-2174" data-mp="2nhjelknkmv1n">
                {completedItems.map(item => <motion.div key={item.id} layout initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="p-4 flex items-center gap-3 opacity-60" data-id="element-2175" data-mp="12zh9vodukid9">
                    <button onClick={() => toggleShoppingListItem(item.id)} className="flex-shrink-0 text-primary" data-id="element-2176" data-mp="t6a2jxzjqsaj">
                      <CheckCircle2 className="w-6 h-6" data-id="element-2177" data-mp="jfmdd9rif2v" />
                    </button>
                    <div className="flex-1 min-w-0" data-id="element-2178" data-mp="5hh5tzb3ogev">
                      <p className="font-medium text-foreground line-through truncate" data-id="element-2179" data-mp="5fxoe5dpjzbi">
                        {item.name}
                      </p>
                    </div>
                    <button onClick={() => removeShoppingListItem(item.id)} className="p-2 text-muted-foreground hover:text-destructive transition-colors" data-id="element-2180" data-mp="2q1r6ns18xm6k">
                      <Trash className="w-4 h-4" data-id="element-2181" data-mp="2zxlwb73w0hiu" />
                    </button>
                  </motion.div>)}
              </AnimatePresence>
            </div>
          </div>}
      </div>

      {/* Sticky Bottom Bar */}
      {shoppingList.length > 0 && <div className="fixed bottom-16 left-0 right-0 p-4 bg-background/95 backdrop-blur-md border-t border-border z-20 pb-safe" data-id="element-2182" data-mp="3nn6hzxrfdsvt">
          <div className="flex items-center justify-between mb-3 px-2" data-id="element-2183" data-mp="3irhnw9y6d46w">
            <span className="text-sm font-medium text-muted-foreground" data-id="element-2184" data-mp="kbh37xp1alx">
              Totaal geschat
            </span>
            <span className="text-xl font-bold text-foreground" data-id="element-2185" data-mp="3ro05iq0yh5o6">
              €{totalCost.toFixed(2)}
            </span>
          </div>
          <button onClick={() => setShowCompleteTripDialog(true)} className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 font-semibold text-primary-foreground shadow-lg transition-colors hover:bg-primary/90" data-id="element-2186" data-mp="k63l8t40jrzr">
            <Check className="h-5 w-5" data-id="element-2187" data-mp="a98p2gv7q3on" />
            <span data-id="element-2188" data-mp="3sl3u84rka9xm">Kassabonnen invoeren</span>
          </button>
        </div>}

      <CompleteTripDialog isOpen={showCompleteTripDialog} items={shoppingList} onClose={() => setShowCompleteTripDialog(false)} onConfirm={handleConfirmTrip} data-id="element-2189" data-mp="177tdofmnpp3" />

      <AnimatePresence data-id="element-2190" data-mp="3nlr7dk0ox2v1">
        {showAIGenerator && <AIListGenerator onClose={() => setShowAIGenerator(false)} onGenerate={handleAIGenerate} data-id="element-2191" data-mp="3nk7pjozdyjr5" />}
      </AnimatePresence>
    </div>;
}
function groupItemsByStore(items: ShoppingListItem[]): Record<string, ShoppingListItem[]> {
  return items.reduce<Record<string, ShoppingListItem[]>>((groups, item) => {
    const store = item.store || 'Overig';
    groups[store] = [...(groups[store] ?? []), item];
    return groups;
  }, {});
}